import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mensaje } from "@/components/mensajes2";
import { Dimmer } from "@/components/spinner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
} from "recharts";

const formatearMonto = (valor) =>
  Number(valor || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const transformarParaGrafica = (item) => {
  const m = item.meses_json;
  return [
    { mes: m.mes_hace_dos, monto: Number(m.impuesto_hace_dos_meses) || 0 },
    { mes: m.mes_anterior, monto: Number(m.impuesto_mes_anterior) || 0 },
    { mes: m.mes_actual, monto: Number(m.impuesto_mes_actual) || 0 },
  ];
};

const calcularPorcentajeCaida = (item) => {
  const m = item.meses_json;
  const inicio = Number(m.impuesto_hace_dos_meses) || 0;
  const fin = Number(m.impuesto_mes_actual) || 0;
  if (inicio <= 0) return 0;
  return ((inicio - fin) / inicio) * 100;
};

const colorSeveridad = (pct) => {
  if (pct >= 60) return "#FF3838";
  if (pct >= 30) return "#E6995A";
  return "#FFF300";
};

const etiquetaSeveridad = (pct) => {
  if (pct >= 60) return "Crítico";
  if (pct >= 30) return "Alto";
  return "Moderado";
};

const StatBox = ({ label, value, color = "#1f4e79", icon }) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center px-1 py-1"
    style={{
      background: "#f8f9fb",
      borderRadius: 8,
      flex: "1 1 calc(50% - 4px)",
      minWidth: 0,
    }}
  >
    <span
      className="text-muted text-truncate w-100 text-center"
      style={{ fontSize: "0.68rem", letterSpacing: 0.2 }}
      title={label}
    >
      {icon} {label}
    </span>
    <span
      className="fw-bold text-truncate w-100 text-center"
      style={{ color, fontSize: "0.82rem", marginTop: 1 }}
      title={value}
    >
      {value}
    </span>
  </div>
);

const GraficaComerciosDeclive = () => {
  const op = require("../../../modulos/datos");
  const token = localStorage.getItem("jwtToken");
    const anioActual = new Date().getFullYear();

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagina, setPagina] = useState(1);
  const porPagina = 8;

  const [mensaje, setMensaje] = useState({
    mostrar: false,
    titulo: "",
    texto: "",
    icono: "",
  });

  const buscarComerciosDeclive = async () => {
    setLoading(true);
    const endpoint = `${op.conexion}/api/industria/consultar-comercios-declive?anio=${anioActual}`;
    axios
      .get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.length > 0) {
          setRecords(response.data);
          setPagina(1);
        }
      })
      .catch(() => {
        setMensaje({
          mostrar: true,
          titulo: "Notificación.",
          texto: "Error al Buscar los Registros",
          icono: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    buscarComerciosDeclive();
  }, []);

  const totalPaginas = Math.ceil(records.length / porPagina) || 1;
  const inicio = (pagina - 1) * porPagina;
  const recordsPagina = records.slice(inicio, inicio + porPagina);

  const generarPaginas = () => {
    const paginas = [];
    const maxVisibles = 5;
    let desde = Math.max(1, pagina - Math.floor(maxVisibles / 2));
    let hasta = Math.min(totalPaginas, desde + maxVisibles - 1);
    if (hasta - desde < maxVisibles - 1) {
      desde = Math.max(1, hasta - maxVisibles + 1);
    }
    if (desde > 1) {
      paginas.push(1);
      if (desde > 2) paginas.push("...");
    }
    for (let i = desde; i <= hasta; i++) paginas.push(i);
    if (hasta < totalPaginas) {
      if (hasta < totalPaginas - 1) paginas.push("...");
      paginas.push(totalPaginas);
    }
    return paginas;
  };

  return (
    <div className="col-12 my-auto pt-3">
      {/* Header general */}
      {/* <div
        className="d-flex justify-content-between align-items-center flex-wrap mb-3 px-2"
        style={{ gap: 10 }}
      >
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: "#1f4e79" }}>
            📉 Comercios en Declive
          </h4>
          <small className="text-muted">
            {records.length} comercio(s) con caída sostenida en los últimos 3 meses
          </small>
        </div>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={buscarComerciosDeclive}
          disabled={loading}
        >
          🔄 Actualizar
        </button>
      </div> */}

      {/* Empty state */}
      {recordsPagina.length === 0 && !loading && (
        <div
          className="text-center text-muted py-5"
          style={{ background: "#f8f9fb", borderRadius: 12 }}
        >
          No hay comercios en declive 🎉
        </div>
      )}

      {/* Grid 2 columnas */}
      <div className="row g-3 px-1">
        {recordsPagina.map((item, index) => {
          const dataGrafica = transformarParaGrafica(item);
          const pct = calcularPorcentajeCaida(item);
          const colorPct = colorSeveridad(pct);
          const m = item.meses_json;
          const posicionGlobal = inicio + index + 1;

          return (
            <div className="col-12 col-md-6" key={item.idcomercio ?? index}>
              <div
                className="card border-0 h-100"
                style={{
                  borderRadius: 14,
                  boxShadow: "0 2px 12px rgba(31,78,121,0.08)",
                  overflow: "hidden",
                }}
              >
                {/* Header card */}
                <div
                  className="d-flex justify-content-between align-items-center px-3 py-2 text-light"
                  style={{
                    background:
                      "linear-gradient(90deg, #1f4e79 0%, #2e86c1 100%)",
                    flexWrap: "nowrap",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  {/* Izquierda: #posición + nombre (truncado) */}
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{
                      minWidth: 0,
                      flex: "1 1 auto",
                      overflow: "hidden",
                    }}
                  >
                    <span
                      className="badge bg-light text-dark fw-bold"
                      style={{ fontSize: "0.7rem", flexShrink: 0 }}
                    >
                      #{posicionGlobal}
                    </span>
                    <span
                      className="mb-0 fw-semibold text-light text-truncate"
                      style={{
                        fontSize: "0.95rem",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={item.nombre || `Comercio: ${item.idcomercio}`}
                    >
                      {item.nombre || item.idcomercio}
                    </span>
                  </div>

                  {/* Derecha: badge severidad (no se encoge) */}
                  <span
                    className="badge fw-bold"
                    style={{
                      background: colorPct,
                      color: "#000000",
                      fontSize: "0.7rem",
                      padding: "5px 8px",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {etiquetaSeveridad(pct)} · ↓ {pct.toFixed(1)}%
                  </span>
                </div>

                {/* Stats 2x2 */}
                <div
                  className="d-flex flex-wrap gap-2 px-3 pt-3"
                  style={{ rowGap: 6 }}
                >
                  <StatBox
                    icon="📅"
                    label={m.mes_hace_dos}
                    value={`Bs ${formatearMonto(m.impuesto_hace_dos_meses)}`}
                  />
                  <StatBox
                    icon="📅"
                    label={m.mes_anterior}
                    value={`Bs ${formatearMonto(m.impuesto_mes_anterior)}`}
                  />
                  <StatBox
                    icon="📅"
                    label={m.mes_actual}
                    value={`Bs ${formatearMonto(m.impuesto_mes_actual)}`}
                    color={colorPct}
                  />
                  <StatBox
                    icon="💸"
                    label="Caída acum."
                    value={`Bs ${formatearMonto(m.caida_total_acumulada)}`}
                    color="#c0392b"
                  />
                </div>

                {/* Gráfica */}
                <div
                  style={{
                    width: "100%",
                    height: 200,
                    padding: "8px 10px 10px",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dataGrafica}
                      margin={{ top: 22, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id={`grad-${item.idcomercio}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#2e86c1"
                            stopOpacity={0.95}
                          />
                          <stop
                            offset="100%"
                            stopColor="#1f4e79"
                            stopOpacity={0.85}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e8eb" />
                      <XAxis
                        dataKey="mes"
                        tick={{ fontSize: 10, fill: "#555" }}
                        axisLine={{ stroke: "#ccc" }}
                        interval={0}
                      />
                      <YAxis
                        tickFormatter={(v) =>
                          v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                        }
                        tick={{ fontSize: 10, fill: "#777" }}
                        width={42}
                        axisLine={{ stroke: "#ccc" }}
                      />
                      <Tooltip
                        formatter={(v) => [`Bs ${formatearMonto(v)}`, "Monto"]}
                        labelFormatter={(l) => `Mes: ${l}`}
                        contentStyle={{
                          borderRadius: 8,
                          border: "1px solid #ddd",
                          fontSize: 12,
                        }}
                      />
                      <Bar
                        dataKey="monto"
                        fill={`url(#grad-${item.idcomercio})`}
                        radius={[6, 6, 0, 0]}
                        maxBarSize={48}
                      >
                        <LabelList
                          dataKey="monto"
                          position="top"
                          formatter={(v) =>
                            v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v
                          }
                          style={{
                            fontSize: 10,
                            fill: "#333",
                            fontWeight: 600,
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-1 my-4 pb-3 flex-wrap">
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === 1}
            onClick={() => setPagina(1)}
            title="Primera"
          >
            ««
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === 1}
            onClick={() => setPagina((p) => p - 1)}
            title="Anterior"
          >
            «
          </button>

          {generarPaginas().map((p, i) =>
            p === "..." ? (
              <span key={`e-${i}`} className="px-2 text-muted">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`btn btn-sm ${
                  p === pagina ? "btn-primary" : "btn-outline-primary"
                }`}
                onClick={() => setPagina(p)}
                style={{ minWidth: 38 }}
              >
                {p}
              </button>
            ),
          )}

          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === totalPaginas}
            onClick={() => setPagina((p) => p + 1)}
            title="Siguiente"
          >
            »
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            disabled={pagina === totalPaginas}
            onClick={() => setPagina(totalPaginas)}
            title="Última"
          >
            »»
          </button>

          <span className="ms-3 text-muted small">
            Página {pagina} de {totalPaginas} · {records.length} comercios
          </span>
        </div>
      )}

      <Mensaje
        mensaje={mensaje}
        onHide={() =>
          setMensaje({ mostrar: false, titulo: "", texto: "", icono: "" })
        }
      />
      <Dimmer active={loading} inverted />
    </div>
  );
};

export default GraficaComerciosDeclive;
